import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { apiGet } from '../api/client';
import type { TimelineEvent } from '../api/types';
import { desktop, isDesktop } from '../desktop/bridge';

/**
 * Canal de notification en direct, côté interface.
 *
 * Son rôle est de prévenir, pas de faire autorité. Ce qui arrive ici est un
 * événement déjà écrit au journal ; l'écran s'en sert pour se mettre à jour
 * sans rafraîchir. Perdre le canal ne rend jamais un écran faux — seulement
 * en retard, et le rattrapage comble ce retard.
 *
 * Deux implémentations derrière la même interface :
 *
 *   — sur le web, une WebSocket ouverte par la page, sur la même origine, avec
 *     le cookie de session ;
 *   — sur le bureau, rien du tout ici : le canal est tenu par le processus
 *     principal, seul détenteur du cookie, et les événements arrivent par le
 *     pont. Le rendu n'a pas de session à présenter, et n'a donc pas à ouvrir
 *     de connexion.
 */

export type LiveState = 'connecte' | 'connexion' | 'interrompu';

type Listener = (event: TimelineEvent) => void;

type RealtimeValue = {
  state: LiveState;
  /** Instant du dernier message reçu ou de la dernière reprise. */
  lastAt: string | null;
  subscribe: (listener: Listener) => () => void;
};

const RealtimeContext = createContext<RealtimeValue | null>(null);

/**
 * Attente avant reconnexion : croissante, plafonnée, et bruitée.
 *
 * Sans plafond, une coupure d'une nuit laisserait un délai de plusieurs
 * heures au matin. Sans bruit, dix postes coupés par le même incident
 * reviendraient tous à la même seconde — et repartiraient ensemble au refus
 * suivant.
 */
const ATTENTE_BASE_MS = 1_000;
const ATTENTE_MAX_MS = 30_000;

function attenteApres(essais: number): number {
  const brut = Math.min(ATTENTE_BASE_MS * 2 ** essais, ATTENTE_MAX_MS);
  return Math.round(brut * (0.7 + Math.random() * 0.6));
}

type CatchUpResponse = { cursor: string; events: TimelineEvent[]; tronque: boolean };

export function RealtimeProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<LiveState>('connexion');
  const [lastAt, setLastAt] = useState<string | null>(null);

  const listeners = useRef(new Set<Listener>());
  /** Curseur de reprise : ce qui a été reçu jusque-là. */
  const cursor = useRef<string | null>(null);
  /** Identifiants déjà distribués, pour qu'un rattrapage ne double pas. */
  const vus = useRef(new Set<string>());

  const distribuer = useCallback((event: TimelineEvent) => {
    // Le rattrapage recouvre volontairement le curseur d'une minute : un même
    // événement peut donc arriver deux fois. Le filtrer ici évite que chaque
    // écran ait à s'en souvenir.
    if (vus.current.has(event.id)) return;
    vus.current.add(event.id);
    // le jeu ne sert qu'à dédoublonner un recouvrement : on le borne
    if (vus.current.size > 2000) {
      vus.current = new Set([...vus.current].slice(-1000));
    }
    for (const listener of [...listeners.current]) listener(event);
  }, []);

  const rattraper = useCallback(async (): Promise<void> => {
    const depuis = cursor.current;
    if (depuis === null) return;
    try {
      const reponse = await apiGet<CatchUpResponse>(
        `/sync/events?depuis=${encodeURIComponent(depuis)}`,
      );
      for (const event of reponse.events) distribuer(event);
      cursor.current = reponse.cursor;
      setLastAt(reponse.cursor);
    } catch {
      // le rattrapage a échoué : le curseur n'avance pas, la prochaine
      // reprise redemandera la même fenêtre. Rien n'est perdu.
    }
  }, [distribuer]);

  // --- bureau : le canal est tenu par le processus principal ---------------
  useEffect(() => {
    if (!isDesktop()) return;
    const bridge = desktop();

    let actif = true;
    bridge.realtime
      .status()
      .then((initial) => {
        if (actif) setState(initial.state);
      })
      .catch(() => {
        // le pont ne répond pas : l'état reste « connexion »
      });

    const desabonner = bridge.realtime.subscribe((message) => {
      if (!actif) return;
      if (message.kind === 'etat') {
        setState(message.state);
        return;
      }
      setLastAt(message.at);
      distribuer(message.event as TimelineEvent);
    });

    return () => {
      actif = false;
      desabonner();
    };
  }, [distribuer]);

  // --- web : la page ouvre elle-même la WebSocket --------------------------
  useEffect(() => {
    if (isDesktop()) return;

    let ferme = false;
    let essais = 0;
    let socket: WebSocket | null = null;
    let minuteur: number | null = null;

    const planifier = (): void => {
      if (ferme) return;
      const attente = attenteApres(essais);
      essais += 1;
      minuteur = window.setTimeout(ouvrir, attente);
    };

    const ouvrir = (): void => {
      if (ferme) return;
      setState((precedent) => (precedent === 'connecte' ? 'connexion' : precedent));

      const protocole = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      socket = new WebSocket(`${protocole}//${window.location.host}/direct`);

      socket.onopen = () => {
        essais = 0;
        setState('connecte');
        // une reprise rattrape ce qui a été écrit pendant la coupure ; à la
        // première connexion il n'y a pas encore de curseur, donc rien à
        // rattraper — l'écran vient d'être chargé
        void rattraper();
      };

      socket.onmessage = (message) => {
        let charge: { type: string; at: string; event?: TimelineEvent };
        try {
          charge = JSON.parse(String(message.data)) as typeof charge;
        } catch {
          return;
        }
        cursor.current = charge.at;
        setLastAt(charge.at);
        if (charge.type === 'evenement' && charge.event !== undefined) {
          distribuer(charge.event);
        }
      };

      socket.onclose = () => {
        if (ferme) return;
        setState('interrompu');
        planifier();
      };

      socket.onerror = () => {
        // onclose suit toujours : la reprise est planifiée là, une seule fois
      };
    };

    ouvrir();

    return () => {
      ferme = true;
      if (minuteur !== null) window.clearTimeout(minuteur);
      socket?.close();
    };
  }, [distribuer, rattraper]);

  const subscribe = useCallback((listener: Listener) => {
    listeners.current.add(listener);
    return () => {
      listeners.current.delete(listener);
    };
  }, []);

  const value = useMemo<RealtimeValue>(
    () => ({ state, lastAt, subscribe }),
    [state, lastAt, subscribe],
  );

  return <RealtimeContext.Provider value={value}>{children}</RealtimeContext.Provider>;
}

export function useRealtime(): RealtimeValue {
  const value = useContext(RealtimeContext);
  if (value === null) {
    throw new Error('useRealtime doit être utilisé dans un RealtimeProvider.');
  }
  return value;
}

/**
 * S'abonne aux événements poussés. Le gestionnaire est gardé dans une
 * référence : un écran peut le redéfinir à chaque rendu sans provoquer de
 * réabonnement, ni manquer un événement entre deux.
 */
export function useLiveEvents(handler: Listener): void {
  const { subscribe } = useRealtime();
  const dernier = useRef(handler);
  dernier.current = handler;

  useEffect(
    () => subscribe((event) => dernier.current(event)),
    [subscribe],
  );
}

export const LIVE_LABELS: Record<LiveState, string> = {
  connecte: 'En direct',
  connexion: 'Connexion…',
  interrompu: 'Direct interrompu',
};
